/**
 * Citation and ActionCard schemas — mirror of backend/app/models/cards.py.
 *
 * Source of truth for what the agent emits alongside prose. SSE payloads
 * are validated against these at the network boundary in lib/api.js, then
 * downstream components consume the inferred types.
 *
 * Keep in sync with the Pydantic models. If you change a field, change it
 * in both places — there is no codegen.
 */

import { z } from 'zod'

// ---------------------------------------------------------------------------
// SourceKind — discriminator that drives renderer dispatch
// ---------------------------------------------------------------------------

export const SourceKindSchema = z.enum([
  'epicor_pdf',
  'pack_slip',
  'spec_sheet',
  'shop_drawing',
  'invoice_dep',
  'invoice_shp',
  'pro_forma',
  'baq_result',
  'list_result',
  'vendor_doc',
  'plaid_transactions',
  'email',
  'sales_order',
  'generic_pdf',
])

export type SourceKind = z.infer<typeof SourceKindSchema>

// Visual treatment per kind — kept central so chips and renderers agree.
// Colors are deliberately muted; do NOT introduce per-kind rainbow palettes.
export const SOURCE_KIND_META: Record<
  SourceKind,
  { label: string; icon: string; tint: string }
> = {
  epicor_pdf:         { label: 'CPO',        icon: 'FileText',     tint: 'slate' },
  pack_slip:          { label: 'Pack Slip',  icon: 'PackageCheck', tint: 'slate' },
  spec_sheet:         { label: 'Spec',       icon: 'FileText',     tint: 'slate' },
  shop_drawing:       { label: 'Drawing',    icon: 'Ruler',        tint: 'slate' },
  invoice_dep:        { label: 'DEP',        icon: 'Receipt',      tint: 'slate' },
  invoice_shp:        { label: 'SHP',        icon: 'Receipt',      tint: 'slate' },
  pro_forma:          { label: 'Pro-Forma',  icon: 'Receipt',      tint: 'slate' },
  baq_result:         { label: 'BAQ',        icon: 'Table',        tint: 'slate' },
  list_result:        { label: 'Table',      icon: 'Table',        tint: 'slate' },
  vendor_doc:         { label: 'Vendor',     icon: 'FileText',     tint: 'slate' },
  plaid_transactions: { label: 'Bank',       icon: 'Landmark',     tint: 'slate' },
  email:              { label: 'Email',      icon: 'Mail',         tint: 'slate' },
  sales_order:        { label: 'SO',         icon: 'ClipboardList',tint: 'slate' },
  generic_pdf:        { label: 'PDF',        icon: 'FileText',     tint: 'slate' },
}

// ---------------------------------------------------------------------------
// Highlight — optional overlay on a citation
// ---------------------------------------------------------------------------

export const HighlightSchema = z.object({
  page: z.number().int().optional(),
  bbox: z.array(z.number()).length(4).optional(),
  row: z.number().int().optional(),
  start: z.number().int().optional(),
  end: z.number().int().optional(),
  label: z.string().optional(),
  color: z.string().optional(),
})

export type Highlight = z.infer<typeof HighlightSchema>

// ---------------------------------------------------------------------------
// Citation
// ---------------------------------------------------------------------------
// Payload is intentionally a `z.record(z.unknown())` at the wire boundary.
// Each renderer narrows it via its own zod schema before consuming. This
// keeps the SSE schema permissive while individual renderers stay strict.

export const CitationSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  kind: SourceKindSchema,
  payload: z.record(z.unknown()).default({}),
  highlights: z.array(HighlightSchema).optional(),
  tool: z.string().optional(),
  fetched_at: z.string().optional(),
})

export type Citation = z.infer<typeof CitationSchema>

// ---------------------------------------------------------------------------
// ActionCard envelope — reserved for PR4
// ---------------------------------------------------------------------------
// We define the shape now so the SSE payload stays stable from PR1. PR1
// never populates `action_card`, but lib/api.js will pass it through if
// present (forward-compat).

export const ActionStatusSchema = z.enum([
  'pending',
  'approved',
  'rejected',
  'executing',
  'succeeded',
  'failed',
])

export const ActionKindSchema = z.enum([
  'create_dep_invoice',
  'create_shp_invoice',
  'create_pro_forma',
  'create_cash_receipt',
  'send_email',
  // Mirrors app/models/cards.py. Order photos carry order_num and
  // photo_xfile_refs, which SendEmailParams has no room for — and the approve
  // endpoint rebuilds params as validated.model_dump(), so anything the schema
  // does not declare is dropped before execution.
  'send_order_photos',
  'attach_to_epicor',
  'update_sales_order',
  'create_quote',
])

export const ValidationIssueSchema = z.object({
  severity: z.enum(['blocking', 'warning']),
  field: z.string().nullish(),
  message: z.string(),
})

export type ValidationIssue = z.infer<typeof ValidationIssueSchema>

export const ActionOperationSchema = z.object({
  type: z.string(),
  params: z.record(z.unknown()),
  editable_fields: z.array(z.string()).default([]),
})

export const ActionPreviewSchema = z.object({
  kind: SourceKindSchema,
  payload: z.record(z.unknown()).default({}),
})

export const ActionCardSchema = z.object({
  id: z.string().min(1),
  kind: ActionKindSchema,
  title: z.string(),
  summary: z.string(),
  status: ActionStatusSchema,
  operation: ActionOperationSchema,
  preview: ActionPreviewSchema,
  evidence: z.array(CitationSchema).default([]),
  validation: z.array(ValidationIssueSchema).default([]),
  created_at: z.string(),
  approved_at: z.string().nullish(),
  approved_by: z.string().nullish(),
  executed_at: z.string().nullish(),
  result: z.unknown().nullish(),
  error: z
    .object({ message: z.string(), retriable: z.boolean() })
    .nullish(),
  high_value_field: z.string().nullish(),
  high_value_threshold: z.number().nullish(),
})

export type ActionCard = z.infer<typeof ActionCardSchema>
export type ActionStatus = z.infer<typeof ActionStatusSchema>
export type ActionKind = z.infer<typeof ActionKindSchema>

// ---------------------------------------------------------------------------
// Safe parsers — used at the SSE boundary
// ---------------------------------------------------------------------------

/** Validate an array of citations from an SSE payload. Drops invalid entries
 * with a console warning rather than throwing — a malformed citation should
 * never break the whole stream. */
export function parseCitationsSafe(raw: unknown): Citation[] {
  if (!Array.isArray(raw)) return []
  const out: Citation[] = []
  for (const item of raw) {
    const r = CitationSchema.safeParse(item)
    if (r.success) {
      out.push(r.data)
    } else {
      // eslint-disable-next-line no-console
      console.warn('Dropping malformed citation:', r.error.issues, item)
    }
  }
  return out
}

/** Validate an ActionCard payload. Returns null on failure — PR4 callers
 * decide how to surface the error. */
export function parseActionCardSafe(raw: unknown): ActionCard | null {
  const r = ActionCardSchema.safeParse(raw)
  if (r.success) return r.data
  // eslint-disable-next-line no-console
  console.warn('Dropping malformed action card:', r.error.issues, raw)
  return null
}
